// Apolo IDE — AdaptersMenu (header global, ⌘E).
//
// Botão sempre visível no canto superior direito + popover ancorado.
//
// Sprint unified-models: o popover agora RENDERIZA o mesmo <AdapterView />
// usado no side-panel — busca, tabs por kind (Texto/Imagem/Vídeo/Áudio),
// TODOS os 33 adapters do catálogo com logos coloridos, status (bolinha
// verde), "+ Adicionar adapter" e edição de API key inline. Fonte única,
// zero duplicação: configurar uma API aqui reflete em todos os dropdowns
// (via apolo:adaptersChanged) e vice-versa.
//
// GitHub OAuth fica num rodapé separado (não é API key — é fluxo OAuth).

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Github, Layers } from 'lucide-react';
import { apiFetch } from '../lib/api.js';
import { isTypingTarget, modKey } from '../lib/keyboardUtils.js';
import AdapterView from './code/side-panel/AdapterView.jsx';
import ModelStatusDot from './shared/ModelStatusDot.jsx';

const SPRING = { type: 'spring', stiffness: 380, damping: 32, mass: 0.6 };

export default function AdaptersMenu() {
  const [open, setOpen] = useState(false);
  const [github, setGithub] = useState({ loading: true, connected: false, login: null });
  const [pos, setPos] = useState({ top: 0, right: 0 });
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const wasOpenRef = useRef(false);

  const refreshGithub = useCallback(async () => {
    const res = await apiFetch('/api/oauth/github/status');
    if (res.ok) {
      setGithub({ loading: false, connected: !!res.data?.connected, login: res.data?.login || null });
    } else {
      setGithub({ loading: false, connected: false, login: null });
    }
  }, []);

  useEffect(() => {
    if (open) refreshGithub();
  }, [open, refreshGithub]);

  // Posição do popover (ancorado no trigger, abre pra baixo, alinhado à direita).
  useLayoutEffect(() => {
    if (!open) return undefined;
    function update() {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setPos({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
    }
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open]);

  // Click-outside + Esc fecham.
  useEffect(() => {
    if (!open) return undefined;
    function onDown(e) {
      if (popoverRef.current?.contains(e.target)) return;
      if (triggerRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    function onKey(e) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Foco entra no dialog ao abrir e volta pro trigger ao fechar (a11y:
  // role=dialog precisa receber foco; senão o leitor de tela continua no botão).
  // wasOpenRef evita roubar foco no mount inicial (open=false na primeira render).
  useEffect(() => {
    if (open) {
      wasOpenRef.current = true;
      const id = requestAnimationFrame(() => popoverRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
    if (wasOpenRef.current) {
      wasOpenRef.current = false;
      triggerRef.current?.focus();
    }
    return undefined;
  }, [open]);

  // ⌘E (sem shift) toggle + evento global apolo:openAdaptersGlobal.
  useEffect(() => {
    function openGlobal() { setOpen(true); }
    function onKey(e) {
      const meta = e.metaKey || e.ctrlKey;
      if (meta && !e.shiftKey && !e.altKey && e.key === 'e') {
        if (isTypingTarget(e.target)) return;
        e.preventDefault();
        setOpen((v) => !v);
      }
    }
    window.addEventListener('apolo:openAdaptersGlobal', openGlobal);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('apolo:openAdaptersGlobal', openGlobal);
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Adapters — configurar provedores de IA"
        aria-haspopup="dialog"
        aria-expanded={open}
        title={`Adapters · ${modKey()}E`}
        style={{ WebkitAppRegion: 'no-drag' }}
        className={`inline-flex h-9 items-center gap-1.5 rounded-apolo-lg border px-2.5 text-xs font-medium backdrop-blur-md transition focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/60 sm:px-3 ${
          open
            ? 'border-apolo-accentBorder bg-apolo-accentMuted text-apolo-textPrimary'
            : 'border-apolo-borderFocus bg-apolo-glass text-apolo-textSecondary hover:border-apolo-accentBorder hover:text-apolo-textPrimary'
        }`}
      >
        <Layers size={15} aria-hidden="true" className="shrink-0" />
        <span className="hidden sm:inline">Adapters</span>
        <kbd
          aria-hidden="true"
          className="ml-0.5 hidden rounded border border-apolo-borderSubtle bg-apolo-surface2 px-1 py-px font-sans text-[10px] leading-none text-apolo-textTertiary sm:inline-block"
        >
          {`${modKey()}E`}
        </kbd>
      </button>

      {createPortal(
        <AnimatePresence>
          {open ? (
            <motion.div
              ref={popoverRef}
              role="dialog"
              aria-label="Adapters"
              tabIndex={-1}
              initial={{ opacity: 0, y: -6, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -6, scale: 0.98 }}
              transition={SPRING}
              className="fixed z-[60] flex w-[min(26rem,calc(100vw-1rem))] flex-col overflow-hidden rounded-xl border border-apolo-borderStrong bg-apolo-surface1/95 backdrop-blur-xl"
              style={{
                top: pos.top,
                right: pos.right,
                // Altura EXPLÍCITA (não só max) — o AdapterView usa h-full +
                // overflow-y-auto interno; sem altura definida na cadeia, o
                // scroll não ativa. Cap em 70vh ou até o bottom da viewport.
                height: `min(70vh, calc(100vh - ${pos.top + 16}px))`,
                boxShadow: '0 16px 48px rgba(0,0,0,0.4)',
                WebkitAppRegion: 'no-drag',
              }}
            >
              {/* Conteúdo principal — reusa o AdapterView (busca + tabs + 33
                  adapters com logos + add + edit API key). Fonte única.
                  min-h-0 permite o flex-1 encolher e o scroll interno rolar. */}
              <div className="min-h-0 flex-1">
                <AdapterView />
              </div>

              {/* Rodapé: GitHub OAuth (fluxo separado de API key) + nota */}
              <div className="shrink-0 border-t border-apolo-borderSubtle">
                <div className="flex items-center gap-2.5 px-3 py-2">
                  <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-apolo-surface2 text-apolo-textSecondary" aria-hidden="true">
                    <Github size={14} />
                  </span>
                  <span className="flex flex-1 flex-col leading-tight">
                    <span className="text-sm font-medium text-apolo-textPrimary">GitHub</span>
                    <span className="flex items-center gap-1 text-[11px]">
                      <ModelStatusDot connected={github.connected} size={6} />
                      <span className={github.connected ? 'text-apolo-success' : 'text-apolo-textTertiary'}>
                        {github.loading ? 'Verificando…' : github.connected ? `@${github.login}` : 'Desconectado'}
                      </span>
                    </span>
                  </span>
                </div>
                <div className="border-t border-apolo-borderSubtle px-3 py-1.5 text-[10px] text-apolo-textTertiary">
                  {`Chaves salvas só no seu Worker apolo-api · ${modKey()}E`}
                </div>
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>,
        document.body,
      )}
    </>
  );
}
